import { useState, useCallback } from 'react';
import { extendVariants } from "@nextui-org/react";
import {
  Autocomplete as AutocompleteNext,
  AutocompleteSection,
  AutocompleteItem
} from "@nextui-org/autocomplete";
import { Controller } from "react-hook-form";
import {useFilter} from "@react-aria/i18n";

const AutocompleteVariant = extendVariants(AutocompleteNext, {
  variants: {
    color: {
      bordered: { // <- add a new color variant
        base: [ // <- Input wrapper slot
          "bg-white hover:bg-white font-normal text-base rounded-full",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[hover=true]:border-gray-500",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-gray-500",
        ],
        listbox: [
          "bg-white hover:bg-white",
          "data-[hover=true]:bg-white",
        ],
        listboxWrapper: [  // <- Input element slot
          "bg-white",
          "text-gray-800 text-base font-semibold",
          "placeholder:text-gray-500",
          "data-[hover=true]:bg-white",
        ],
      },
      custom: { // <- add a new color variant
        base: [ // <- Input wrapper slot
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-800 text-base font-semibold",
          "data-[placeholder=true]:text-gray-800 text-base font-semibold",
          "data-[hover=true]:border-primary",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-primary",
        ],
        listboxWrapper: [  // <- Input element slot
          "bg-white",
          "text-gray-800 text-base font-semibold",
          "placeholder:text-gray-500",
        ],
      },
      customDanger: { // <- add a new color variant
        listbox: [ // <- Input wrapper slot
          "border-b border-danger rounded-none",
          "shadow-none",
          "transition-colors",
          "focus-within:bg-white",
          "data-[editable=true]:text-gray-800 text-base font-semibold",
          "data-[placeholder=true]:text-gray-800 text-base font-semibold",
          "data-[hover=true]:border-primary",
          "data-[hover=true]:bg-white",
          "group-data-[focus=true]:bg-white border-danger",
        ],
        label: [
          "text-primary font-semibold text-base"
        ],
        listboxWrapper: [  // <- Input element slot
          "text-gray-800 text-base font-semibold",
          "placeholder:text-gray-500",
        ],
      },
    },
  },
  defaultVariants: { // <- modify/add default variants
    color: "bordered",
  },
});

const Autocomplete = props => {
  const { name = '', options = [], control = null, ...rest } = props;
  const [fieldState, setFieldState] = useState({
    selectedKey: "",
    inputValue: "",
    items: options.slice(0, 50),
  });
  const {startsWith, contains} = useFilter({sensitivity: "base"});

  const filter = useCallback((value) => {
    return options.filter((item) => contains(item.label, value)).slice(0, 100);
  }, [options]);

  // Specify how each of the Autocomplete values should change when an
  // option is selected from the list box
  const onSelectionChange = (key, onChange) => {
    onChange(key);
    setFieldState((prevState) => {
      let selectedItem = prevState.items.find((option) => option.value === key);

      return {
        inputValue: selectedItem?.label || "",
        selectedKey: key,
        items: options.filter((item) => startsWith(item.label, selectedItem?.label || "")),
      };
    });
  };

  // Specify how each of the Autocomplete values should change when the input
  // field is altered by the user
  const onInputChange = (value) => {
    setFieldState((prevState) => ({
      inputValue: value,
      selectedKey: value === "" ? null : prevState.selectedKey,
      items: filter(value),
    }));
  };

  // Show entire list if user opens the menu manually
  const onOpenChange = (isOpen, menuTrigger) => {
    if (menuTrigger === "manual" && isOpen) {
      setFieldState((prevState) => ({
        inputValue: prevState.inputValue,
        selectedKey: prevState.selectedKey,
        items: options,
      }));
    }
  };
  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <AutocompleteVariant
            inputValue={fieldState.inputValue}
            items={fieldState.items}
            selectedKey={fieldState.selectedKey}
            onInputChange={onInputChange}
            onOpenChange={onOpenChange}
            onSelectionChange={(value) => onSelectionChange(value, onChange)}
            variant='bordered'
            inputProps={{
              classNames: {
                base: "ml-0 pl-0 rounded-full",
                inputWrapper: [
                  "bg-white hover:bg-white border border-gray-500 text-gray-800 rounded-lg",
                  "data-[hover=true]:border-gray-500",
                  "data-[hover=true]:bg-white",
                ]
              },
            }}
            {...rest}
          >
            {(item) => <AutocompleteItem key={item.value}>{item.label}</AutocompleteItem>}
          </AutocompleteVariant>
        )}
      />
     
    </>
  );
};

export default Autocomplete;
